import { Check } from 'lucide-react';
import { useState } from 'react';
import {
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { formatRM, parseRM } from '@/features/billing/money';
import type { PatientSummary } from '@/features/patients/types';
import {
  useAppointmentActions,
  useDoctors,
  useServices,
} from '@/features/queue/api';
import type { Appointment } from '@/features/queue/types';
import { isApiError } from '@/lib/api';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import { bookingConflict } from './booking-format';
import { toIso, todayKl, whenLabel } from './time';
import styles from './calendar.module.css';

/**
 * "New booking" (#17): patient, service, date, time, branch and repeat.
 * Umrah and Hajj vaccination asks for the travel date and checks the 10-day
 * rule; Friday prayer times, holidays, closures and leave cannot be booked
 * (checked by the API). A repeat creates the whole series.
 */
export function NewBookingDrawer({
  open,
  onClose,
  patient: initialPatient = null,
}: {
  open: boolean;
  onClose: () => void;
  patient?: PatientSummary | null;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="New booking">
      {open && <Form onDone={onClose} initialPatient={initialPatient} />}
    </Dialog>
  );
}

function Form({
  onDone,
  initialPatient,
}: {
  onDone: () => void;
  initialPatient: PatientSummary | null;
}) {
  const { me } = useSession();
  const { selected } = useBranch();
  const branches = me?.branches ?? [];
  const { data: services } = useServices();
  const { data: doctors } = useDoctors();
  const a = useAppointmentActions();
  const toast = useToast();
  const [patient, setPatient] = useState<PatientSummary | null>(initialPatient);
  const [service, setService] = useState('');
  const [doctor, setDoctor] = useState('');
  const [date, setDate] = useState(todayKl());
  const [time, setTime] = useState('09:15');
  const [branch, setBranch] = useState(selected?.id ?? branches[0]?.id ?? '');
  const [repeat, setRepeat] = useState<Appointment['repeat']>('once');
  const [travel, setTravel] = useState('');
  const [deposit, setDeposit] = useState('');
  const [problem, setProblem] = useState<{
    title?: string;
    message: string;
  } | null>(null);
  const svc = services?.find((s) => s.id === service);
  const depositSen = deposit.trim() ? parseRM(deposit) : null;

  async function book() {
    setProblem(null);
    try {
      const r = await a.book.mutateAsync({
        patientId: patient!.id,
        serviceId: service,
        doctorStaffId: doctor || undefined,
        startsAt: toIso(date, time),
        repeat,
        travelDate: travel || undefined,
        depositSen: depositSen ?? undefined,
        branchId: branch,
      });
      const moved = r.series.filter((s) => s.movedFrom);
      toast.show({
        tone: 'success',
        title:
          r.series.length > 1
            ? `Booked a series of ${r.series.length}`
            : `Booked ${whenLabel(r.startsAt)}`,
        body:
          [
            r.depositReason &&
              r.deposit &&
              `${r.depositReason} ${formatRM(r.deposit.amountSen)} due.`,
            moved.length > 0 &&
              `${moved.length} later ${moved.length === 1 ? 'date was' : 'dates were'} moved to the next open day.`,
          ]
            .filter(Boolean)
            .join(' ') || undefined,
      });
      onDone();
    } catch (err) {
      setProblem(
        bookingConflict(err) ?? {
          message: isApiError(err) ? err.message : 'Not booked.',
        },
      );
    }
  }

  const needsDeposit =
    isApiError(a.book.error) && a.book.error.code === 'DEPOSIT_AMOUNT_REQUIRED';

  return (
    <div className={styles.stack}>
      <PatientPicker value={patient} onChange={setPatient} />
      <Field label="Service">
        <Select
          value={service}
          onChange={(e) => setService(e.target.value)}
          placeholder="Choose the service"
          options={(services ?? []).map((s) => ({
            value: s.id,
            label: s.name,
          }))}
        />
      </Field>
      <Field label="Doctor">
        <Select
          value={doctor}
          onChange={(e) => setDoctor(e.target.value)}
          options={[
            { value: '', label: 'Any' },
            ...(doctors ?? []).map((d) => ({ value: d.id, label: d.fullName })),
          ]}
        />
      </Field>
      <Field label="Date">
        <Input
          type="date"
          value={date}
          min={todayKl()}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>
      <Field label="Time">
        <Input
          type="time"
          value={time}
          step={300}
          onChange={(e) => setTime(e.target.value)}
        />
      </Field>
      <div className={styles.section}>
        <p className="eyebrow">Branch</p>
        <ChipToggle
          label="Branch"
          single
          options={branches.map((b) => ({ value: b.id, label: b.name }))}
          selected={[branch]}
          onChange={([b]) => setBranch(b)}
        />
      </div>
      <div className={styles.section}>
        <p className="eyebrow">Repeat</p>
        <ChipToggle
          label="Repeat"
          single
          options={[
            { value: 'once', label: 'Once' },
            { value: 'every_3_months', label: 'Every 3 months' },
            { value: 'every_6_months', label: 'Every 6 months' },
          ]}
          selected={[repeat]}
          onChange={([r]) => setRepeat(r)}
        />
      </div>
      <Field
        label={
          <>
            Travel date (Umrah and Hajj){' '}
            {!svc?.requiresTravelDate && (
              <span className={styles.optional}>optional</span>
            )}
          </>
        }
        hint="Date of arrival in Saudi Arabia: the vaccine is given at least 10 days before."
      >
        <Input
          type="date"
          value={travel}
          onChange={(e) => setTravel(e.target.value)}
        />
      </Field>
      {(svc?.depositSen || needsDeposit) && (
        <Field
          label="Deposit (RM)"
          hint={
            svc?.depositSen
              ? `Deposit for this service: ${formatRM(svc.depositSen)}.`
              : 'Deposit needed after repeated no-shows.'
          }
        >
          <Input
            inputMode="decimal"
            value={deposit}
            placeholder={svc?.depositSen ? String(svc.depositSen / 100) : '20'}
            onChange={(e) => setDeposit(e.target.value)}
          />
        </Field>
      )}
      {problem && (
        <p className={styles.note} role="alert">
          {problem.title && (
            <>
              <strong>{problem.title}</strong>
              <br />
            </>
          )}
          {problem.message}
        </p>
      )}
      <div className={styles.actions}>
        <Button onClick={onDone}>Cancel</Button>
        <Button
          variant="primary"
          icon={<Check size={16} />}
          disabled={!patient || !service || !date || !time || !branch}
          loading={a.book.isPending}
          onClick={() => void book()}
        >
          Book
        </Button>
      </div>
    </div>
  );
}
